import React, { useEffect, useRef, useState } from "react";
import "../assets/css/Facilities.css";

import facility1 from "../assets/img/campus/library.png";
import facility2 from "../assets/img/campus/laboratories.png";
import facility3 from "../assets/img/campus/classrooms.png";
import facility4 from "../assets/img/campus/sports.png";
import facility5 from "../assets/img/campus/hostels.png";
import facility6 from "../assets/img/campus/halls.png";
import facility7 from "../assets/img/campus/courtroom.png";
import facility8 from "../assets/img/campus/student-support.png";

import learn from "../assets/img/campus/classrooms-4.png";
import discover from "../assets/img/campus/laboratories-2.png";
import move from "../assets/img/campus/sports-2.png";
import live from "../assets/img/campus/hostels-2.png";

const facilities = [
  {
    name: "Central Library",
    kicker: "Academic resource",
    icon: "⌑",
    image: facility1,
    description:
      "A focused learning environment for higher education with dedicated areas for reading, individual study and collaborative learning.",
    points: [
      "Books, journals and publications",
      "E-books and digital learning resources",
      "Quiet and collaborative study zones",
    ],
  },
  {
    name: "Advanced Laboratories",
    kicker: "Spaces For Practical Learning",
    icon: "⚗",
    image: facility2,
    description:
      "Multidisciplinary laboratories that enable supervised experimentation, hands-on practice and research-led exploration essential for academic programmes.",
    points: [
      "Science and research laboratories",
      "Engineering and pharmaceutical labs",
      "Language and management learning spaces",
    ],
  },
  {
    name: "Smart Classrooms",
    kicker: "Dynamic Learning Environments",
    icon: "▣",
    image: facility3,
    description:
      "Well-equipped classrooms support presentations, and collaboration, offering a balance of traditional and tech-assisted teaching.",
    points: [
      "Tech-assisted teaching",
      "Comfortable learning environment",
      "Spaces for discussion and teamwork",
    ],
  },
  {
    name: "Sports & Fitness",
    kicker: "Active campus LIFE",
    icon: "◉",
    image: facility4,
    description:
      "Spacious playgrounds, sports-courts, indoor clubs and fitness arenas encourage participation, holistic wellbeing and competitive spirit.",
    points: [
      "Cricket, football and basketball",
      "Badminton and indoor games",
      "Gymnasium and fitness equipment",
    ],
  },
  {
    name: "Student Hostels",
    kicker: "Residential life",
    icon: "⌂",
    image: facility5,
    description:
      "Comfortable accommodation and shared study areas provide a secure, convenient and community-oriented residential experience.",
    points: [
      "Separate accommodation facilities",
      "Furnished living and study spaces",
      "Support for a comfortable campus life",
    ],
  },
  {
    name: "Auditorium & Seminar Hall",
    kicker: "From Ideas To Expression",
    icon: "◫",
    image: facility6,
    description:
      "Event-ready spaces to host workshops, expert-led sessions, conferences, cultural programmes and annual celebrations.",
    points: [
      "Academic and industry events",
      "Workshops and guest sessions",
      "Cultural and student programmes",
    ],
  },
  {
    name: "Moot Court",
    kicker: "Practical Legal Education",
    icon: "⚖",
    image: facility7,
    description:
      "A simulated courtroom environment to help law students practise advocacy, legal reasoning and professional courtroom procedures.",
    points: [
      "Advocacy and trial simulations",
      "Legal research and presentation",
      "Professional courtroom experience",
    ],
  },
  {
    name: "Student Support",
    kicker: "Campus Essentials",
    icon: "✦",
    image: facility8,
    description:
      "Transport, cafeteria, medical support, clubs and placement services make everyday campus life more connected and convenient.",
    points: [
      "Campus transportation",
      "Cafeteria and medical support",
      "Training, placement and student clubs",
    ],
  },
];

const padNumber = (value) => String(value).padStart(2, "0");

export default function Facilities() {
  const sectionRef = useRef(null);
  const facilityRailRef = useRef(null);
  const railRefs = useRef([]);
  const [campusKey, setCampusKey] = useState("raipur");
  const [activeFacility, setActiveFacility] = useState(0);
  const [isChanging, setIsChanging] = useState(false);

  const facility = facilities[activeFacility];

  const changeCampus = (key) => {
    if (key === campusKey) return;
    setIsChanging(true);
    window.setTimeout(() => {
      setCampusKey(key);
      setIsChanging(false);
    }, 180);
  };

  const showFacility = (index) => {
    const nextIndex = (index + facilities.length) % facilities.length;
    setIsChanging(true);
    window.setTimeout(() => {
      setActiveFacility(nextIndex);
      setIsChanging(false);

      const rail = facilityRailRef.current;
      const activeCard = railRefs.current[nextIndex];

      if (rail && activeCard) {
        const centeredPosition =
          activeCard.offsetLeft -
          (rail.clientWidth - activeCard.offsetWidth) / 2;

        rail.scrollTo({
          left: Math.max(0, centeredPosition),
          behavior: "smooth",
        });
      }
    }, 170);
  };

  useEffect(() => {
    const root = sectionRef.current;
    if (
      !root ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const elements = root.querySelectorAll("[data-reveal]");
    elements.forEach((element) => element.classList.add("facility-reveal"));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("facility-reveal--visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="campus-facilities"
      id="campus-facilities"
      ref={sectionRef}
    >
      <div className="facility-orb facility-orb--one" aria-hidden="true" />
      <div className="facility-orb facility-orb--two" aria-hidden="true" />

      <div className="facility-container">
        <header className="facility-heading" data-reveal>
          <div className="facility-heading__title">
            <span className="facility-eyebrow">
              <i /> Campus facilities
            </span>
            <h2>
              Welcoming Growth  <em>From Every Dimension</em>
            </h2>
          </div>
          <div className="facility-heading__copy">
            <p>
              From focused learning and practical discovery to recreation,
              wellbeing and community life, our campuses create an environment
              where students can learn confidently and live fully.
            </p>
            <a href="#facility-explorer" className="facility-text-link">
              Explore the experience <span>↓</span>
            </a>
          </div>
        </header>

        <div
          className="facility-explorer"
          id="facility-explorer"
          data-reveal
        >
          <div className="facility-explorer__intro">
            <span className="facility-eyebrow">
              <i /> Explore facilities
            </span>
            <h3>
              An Ecosystem <em>Of Thoughtful Progression</em>
            </h3>
            <p>
              Explore life at the best university in Raipur through our facilities, and discover how each space contributes to academic achievement, personal wellbeing and a thriving campus life.
            </p>
            <div className="facility-explorer__controls">
              <button
                type="button"
                onClick={() => showFacility(activeFacility - 1)}
                aria-label="Previous facility"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => showFacility(activeFacility + 1)}
                aria-label="Next facility"
              >
                →
              </button>
              <span>
                <strong>{padNumber(activeFacility + 1)}</strong> /{" "}
                <small>{padNumber(facilities.length)}</small>
              </span>
            </div>
          </div>

          <div
            className={`facility-explorer__display ${isChanging ? "facility-fade" : ""
              }`}
          >
            <div className="facility-display__image">
              <img src={facility.image} alt={facility.name} />
              <span aria-hidden="true"><i className={`ti ti-${facility.icon}`}></i></span>
            </div>
            <article className="facility-display__content">
              <span>{facility.kicker}</span>
              <h4>{facility.name}</h4>
              <p>{facility.description}</p>
              <ul>
                {facility.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          </div>

          <div
            className="facility-rail"
            ref={facilityRailRef}
            role="tablist"
            aria-label="Campus facilities"
          >
            {facilities.map((item, index) => (
              <button
                key={item.name}
                ref={(element) => {
                  railRefs.current[index] = element;
                }}
                type="button"
                role="tab"
                aria-selected={activeFacility === index}
                className={activeFacility === index ? "active" : ""}
                onClick={() => showFacility(index)}
              >
                <span>{padNumber(index + 1)}</span>
                <strong>{item.name}</strong>
              </button>
            ))}
          </div>
        </div>

        <div className="facility-stories">
          <article className="facility-story facility-story--large" data-reveal>
            <div className="facility-story__image">
              <img
                src={learn}
                alt="Technology-enabled university classroom"
                loading="lazy"
              />
            </div>
            <div className="facility-story__content">
              <span>01 · Learn</span>
              <h3>Classrooms That Support Proactive Learning</h3>
              <p>
                Tech-enabled classrooms -  support presentations, discussion and collaborative work.
              </p>

            </div>
          </article>

          <article className="facility-story facility-story--reverse" data-reveal>
            <div className="facility-story__content">
              <span>02 · Discover</span>
              <h3>Where Theories Transform Into Experiences.</h3>
              <p>
                Practice your learnings in our up-to-date computer, science, engineering, research, language and pharmaceutical laboratories, and develop market-demanded capabilities.
              </p>
              <div className="facility-story__tags">
                {["Science", "Engineering", "Computing", "Pharmacy"].map(
                  (tag) => (
                    <small key={tag}>{tag}</small>
                  )
                )}
              </div>
              <a href="/Laboratories">
                Explore More About Laboratories <i>↗</i>
              </a>
            </div>
            <div className="facility-story__image">
              <img
                src={discover}
                alt="Students learning in a science laboratory"
                loading="lazy"
              />
            </div>
          </article>

          <div className="facility-life-grid">
            <article
              className="facility-life-card facility-life-card--wide"
              data-reveal
            >
              <img
                src={move}
                alt="Outdoor university sports facilities"
                loading="lazy"
              />
              <div>
                <span>Move</span>
                <h4>Sports &amp; recreation</h4>
                <p>
                  Playgrounds, courts, indoor games and a gymnasium support an
                  active campus lifestyle.
                </p>
              </div>
            </article>
            <article className="facility-life-card" data-reveal>
              <img
                src={live}
                alt="Comfortable student accommodation"
                loading="lazy"
              />
              <div>
                <span>Live</span>
                <h4>Student hostels</h4>
                <p>
                  Safe and comfortable residential facilities for focused,
                  connected campus living.
                </p>
              </div>
            </article>
            <article
              className="facility-life-card facility-life-card--dark"
              data-reveal
            >
              <div className="facility-life-card__icon" aria-hidden="true">
                ✦
              </div>
              <div>
                <span>Connect</span>
                <h4>Clubs &amp; student life</h4>
                <p>
                  Cultural, scientific and student-led activities encourage
                  participation beyond academics.
                </p>
              </div>
            </article>
          </div>
        </div>

        <footer className="facility-cta" data-reveal>
          <div>
            <span className="facility-eyebrow">
              <i /> Visit the campus
            </span>
            <h3>See where your university journey could begin.</h3>
          </div>
          <div className="facility-cta__actions">
            <a href="/raipur-campus">
              Explore Raipur <span>↗</span>
            </a>
            <a href="/gariyaband-campus">
              Explore Gariyaband <span>↗</span>
            </a>
          </div>
        </footer>
      </div>
    </section>
  );
}